import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const COMPANY = {
  name: 'TERRE ET MER LOCATION DE LINGE',
  shortName: 'TERRE ET MER',
  email: 'contact@terreetmer-locationdelinge.fr',
  hours: 'du lundi au vendredi de 10h à 12h et de 14h à 17h, samedi de 10h à 12h',
};

const REPLACEMENT_TABLE = [
  // Pièces composant les Kits Draps (S / M / L / XL)
  { article: 'Drap plat 90 cm (Kit S)', price: '9,00 €' },
  { article: 'Drap plat 140 cm (Kit M)', price: '12,00 €' },
  { article: 'Drap plat 160 cm (Kit L)', price: '14,00 €' },
  { article: 'Drap plat 180 cm (Kit XL / King)', price: '15,00 €' },
  { article: 'Housse de couette 90 cm (Kit S)', price: '22,00 €' },
  { article: 'Housse de couette 140 cm (Kit M)', price: '28,00 €' },
  { article: 'Housse de couette 160 cm (Kit L)', price: '30,00 €' },
  { article: 'Housse de couette 180 cm (Kit XL / King)', price: '32,00 €' },
  { article: 'Taie d\'oreiller', price: '3,00 €' },
  // Linge de toilette (catalogue actuel)
  { article: 'Serviette de bain 70 × 140 cm (Kit serviettes)', price: '12,00 €' },
  { article: 'Tapis de bain 50 × 70 cm', price: '8,00 €' },
  { article: 'Torchon 40 × 40 cm (l\'unité)', price: '2,00 €' },
  // Emballage
  { article: 'Sac à linge', price: '10,00 €' },
];

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 mb-12" data-testid={`cgv-${id}`}>
    <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium text-[#2D3E50] mb-4 pb-3 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-[#475569] leading-relaxed text-[15px]">
      {children}
    </div>
  </section>
);

const CGVPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm" data-testid="cgv-back">
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
          <span className="text-xs uppercase tracking-[0.25em] text-[#E8945A] font-medium">
            Cadre légal
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium mt-4">
            Conditions Générales de Vente
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl">
            {COMPANY.name} — Service de location et livraison de linge pour les locations saisonnières.
          </p>
          <p className="text-white/50 text-sm mt-6">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
          <div className="rounded-xl border-2 border-[#E8945A]/30 bg-[#FFFBF5] p-5 flex gap-3">
            <AlertCircle className="h-5 w-5 text-[#E8945A] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#2D3E50] text-sm">Aucun remboursement</p>
              <p className="text-xs text-[#64748B] mt-1">Toute commande validée et payée est ferme et définitive (voir Art. 22).</p>
            </div>
          </div>
          <div className="rounded-xl border-2 border-[#5493CC]/30 bg-[#F2F8FD] p-5 flex gap-3">
            <ShieldCheck className="h-5 w-5 text-[#5493CC] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#2D3E50] text-sm">Caution de 150 € maximum</p>
              <p className="text-xs text-[#64748B] mt-1">Empreinte de carte conservée pour couvrir vol/dégradation du linge (voir Art. 16).</p>
            </div>
          </div>
        </div>

        {/* Articles */}
        <Section id="art-1" title="Article 1 – Intégralité">
          <p>
            Les présentes conditions générales expriment l'intégralité des obligations des parties.
            En ce sens, le CLIENT est réputé les accepter sans réserve. Les présentes conditions
            générales s'appliquent à l'exclusion de toutes autres conditions, et notamment celles
            applicables pour les ventes en magasin ou au moyen d'autres circuits de distribution
            et de commercialisation. Elles sont accessibles sur le site internet de {COMPANY.name}
            et prévaudront, le cas échéant, sur toute autre version ou tout autre document
            contradictoire. {COMPANY.name} et le CLIENT conviennent que les présentes conditions
            générales régissent exclusivement leur relation. {COMPANY.name} se réserve le droit
            de modifier ponctuellement ses conditions générales.
          </p>
        </Section>

        <Section id="art-2" title="Article 2 – Contenu">
          <p>
            Les présentes conditions générales ont pour objet de définir les droits et obligations
            des parties dans le cadre de la location en ligne de produits proposés par le prestataire,
            à partir du site internet de {COMPANY.name}. Elles concernent les services suivants :
            location de linge à usage personnel, vente de produits accessoires à titre principal
            tels que des couettes, oreillers, linge de lit, produits d'entretien… Les présentes
            conditions ne concernent que les transactions de location effectuées par les acheteurs
            situés en France et livrés exclusivement sur le territoire français. Pour toute livraison
            hors de France, il convient d'adresser un message à cette fin à <a href={`mailto:${COMPANY.email}`} className="text-[#E8945A] underline">{COMPANY.email}</a>.
          </p>
        </Section>

        <Section id="art-3" title="Article 3 – Informations précontractuelles">
          <p>
            <strong>3.1</strong> Le CLIENT reconnaît avoir eu communication, préalablement à la
            passation de sa commande et à la conclusion du contrat, d'une manière lisible et
            compréhensible, des présentes conditions générales et de toutes les informations
            listées à l'article L. 221-5 du code de la consommation.
          </p>
          <p>
            <strong>3.2</strong> Sont transmises au CLIENT, de manière claire et compréhensible,
            les informations suivantes :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>les caractéristiques essentielles du service ;</li>
            <li>le prix du service ;</li>
            <li>tous les frais supplémentaires de transport, de livraison ou d'affranchissement et tous les autres frais éventuels, notamment le montant des pénalités de retard dans le retour des produits ;</li>
            <li>les informations relatives à l'identité du prestataire, à ses coordonnées postales, téléphoniques et électroniques, et à ses activités, celles relatives aux garanties légales, aux fonctionnalités du contenu numérique et, le cas échéant, à son interopérabilité, à l'existence et aux modalités de mise en œuvre des garanties et autres conditions contractuelles ;</li>
            <li>les modalités de paiement, de livraison et d'exécution du contrat, ainsi que les modalités prévues par le professionnel pour le traitement des réclamations ;</li>
            <li>l'existence et les modalités d'exercice de la garantie légale de conformité prévue aux articles L. 217-1 et suivants du code de la consommation, ainsi que de la garantie commerciale et du service après-vente respectivement visés aux articles L. 217-15 et L. 217-17 du code de la consommation ;</li>
            <li>la durée du contrat.</li>
          </ul>
          <p>
            Pour toute question relative au suivi d'une commande, le CLIENT peut contacter
            {' '}{COMPANY.name} à l'adresse <a href={`mailto:${COMPANY.email}`} className="text-[#E8945A] underline">{COMPANY.email}</a> ({COMPANY.hours}).
          </p>
        </Section>

        <Section id="art-4" title="Article 4 – La commande">
          <p>
            Le CLIENT passe sa commande en ligne, à partir du catalogue en ligne et au moyen
            des indications qu'il a fournies (date et localisation) sur le site internet.
            Pour que la commande soit validée, le CLIENT devra accepter, en cliquant à l'endroit
            indiqué sur le site, les présentes conditions générales. Son acceptation entraînera
            l'envoi d'un mail de confirmation de la part du prestataire sur sa messagerie
            électronique et dans son espace personnel.
          </p>
          <p>La vente sera considérée comme définitive :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>après l'envoi par courrier électronique au CLIENT de la confirmation de l'acceptation de la commande par {COMPANY.name} ;</li>
            <li>et après encaissement de l'intégralité du prix.</li>
          </ul>
          <p>
            Toute commande vaut acceptation des prix et descriptions des prestations proposées.
            Dans certains cas, notamment défaut de paiement, adresse erronée ou autre problème
            sur le compte du CLIENT, {COMPANY.name} se réserve le droit de bloquer la commande
            jusqu'à la résolution du problème. En cas d'impossibilité de réalisation de la
            prestation, notamment en cas d'erreur dans le traitement des commandes ou quant à
            la disponibilité des produits, le CLIENT en sera informé par courrier électronique.
          </p>
        </Section>

        <Section id="art-5" title="Article 5 – Signature électronique">
          <p>
            La fourniture en ligne des coordonnées bancaires du CLIENT et la validation finale
            de la commande vaudront preuve de l'accord du CLIENT et vaudront :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>exigibilité des sommes dues au titre du bon de commande ;</li>
            <li>signature et acceptation expresse de toutes les opérations effectuées.</li>
          </ul>
          <p>
            En cas d'utilisation frauduleuse des coordonnées bancaires, le CLIENT est invité,
            dès le constat de cette utilisation, à contacter {COMPANY.name} à l'adresse suivante :
            {' '}<a href={`mailto:${COMPANY.email}`} className="text-[#E8945A] underline">{COMPANY.email}</a>.
          </p>
        </Section>

        <Section id="art-6" title="Article 6 – Délai de commande – Frais de livraison express">
          <p>
            Les commandes doivent être passées <strong>au minimum 3 jours pleins avant la date
            d'arrivée souhaitée</strong>, avec une coupure quotidienne à <strong>16h00 (heure de Paris)</strong>,
            pour bénéficier de la livraison standard gratuite. Toute commande passée à partir de 16h00 décale
            le jour de référence au jour ouvrable suivant.
          </p>
          <p>
            Exemple : pour une livraison gratuite jeudi, la commande doit être passée au plus tard
            le lundi avant 16h00. Une commande passée le lundi à 16h01 ne bénéficiera de la livraison
            gratuite qu'à partir du vendredi.
          </p>
          <p>
            Toute commande ne respectant pas ce délai fera l'objet d'un
            <strong> supplément forfaitaire de 40 € TTC</strong> au titre des frais de livraison
            express, automatiquement ajouté au panier lors de la validation et accepté par le
            CLIENT au moment du paiement.
          </p>
        </Section>

        <Section id="art-7" title="Article 7 – Informations sur les produits">
          <p>
            Les prestations régies par les présentes conditions générales sont celles qui figurent
            sur le site internet de {COMPANY.name}. Elles sont proposées dans la limite des
            disponibilités du prestataire. Les prestations sont décrites et présentées avec la
            plus grande exactitude possible. Toutefois si des erreurs ou omissions ont pu se
            produire quant à cette présentation, la responsabilité du prestataire ne pourrait
            être engagée. {COMPANY.name} se réserve le droit de proposer des produits équivalents.
            Aucune des descriptions présentes sur le site n'a de valeur contractuelle et ne
            saurait engager celui-ci vis-à-vis des CLIENTS.
          </p>
        </Section>

        <Section id="art-8" title="Article 8 – Tarifs">
          <p>
            <strong>8.1 Prix.</strong> {COMPANY.name} se réserve le droit de modifier ses prix
            à tout moment mais s'engage à appliquer les tarifs en vigueur indiqués au moment de
            la commande, sous réserve de disponibilité à cette date. Les prix sont indiqués en
            euros TTC. Ils ne tiennent pas compte des frais de livraison express éventuels
            (Art. 6), facturés en supplément et indiqués avant la validation de la commande.
            Les prix tiennent compte de la TVA applicable au jour de la commande. Le paiement
            de la totalité du prix doit être réalisé lors de la commande. À aucun moment,
            les sommes versées ne pourront être considérées comme des arrhes ou des acomptes.
          </p>
          <p>
            <strong>8.2 Calcul du prix.</strong> Le prix de location des produits correspond
            à un forfait par séjour. Toute utilisation supérieure à la durée prévue fera
            l'objet d'accords spéciaux et d'une facturation complémentaire. La restitution
            anticipée du linge par rapport à la durée de location choisie ne donnera lieu à
            aucune réduction du prix.
          </p>
        </Section>

        <Section id="art-9" title="Article 9 – Modalités et délais de paiement">
          <p>
            Le règlement de la commande s'effectue exclusivement en ligne, par carte bancaire,
            au moment de la validation de la commande, en totalité. {COMPANY.name} se réserve
            le droit de suspendre toute gestion de commande et toute livraison en cas de refus
            d'autorisation de paiement de la part des organismes officiellement accrédités ou
            en cas de non-paiement.
          </p>
          <p>
            En cas de refus de paiement, opposition à prélèvement, et plus généralement en cas
            de non-paiement total ou partiel, le CLIENT sera redevable de plein droit d'intérêts
            égaux au taux d'intérêt appliqué par la Banque Centrale Européenne (BCE) à son
            opération de refinancement la plus récente, majoré de 10 points de pourcentage,
            ainsi que d'une pénalité forfaitaire de 50 €.
          </p>
        </Section>

        <Section id="art-10" title="Article 10 – Livraison">
          <p>
            La livraison s'entend du transfert au CLIENT de la possession physique ou du
            contrôle du bien. Elle n'est faite qu'après confirmation du paiement par
            l'organisme bancaire du prestataire.
          </p>
          <p>
            La livraison s'effectue directement dans le logement de location indiqué par le
            CLIENT lors de la commande. Le CLIENT devra veiller à l'exactitude des informations
            (adresse, code d'accès, instructions). En cas d'absence ou d'impossibilité d'accès
            au logement, seuls les documents établis par {COMPANY.name} ou son substitué quant
            à la décomposition (nombre, quantité, qualité) du linge livré ou restitué feront foi.
          </p>
        </Section>

        <Section id="art-11" title="Article 11 – Erreurs de livraison">
          <p>
            Le CLIENT devra formuler auprès de {COMPANY.name} le jour même de la livraison
            toute réclamation d'erreur de livraison et/ou de non-conformité des produits en
            nature ou en qualité par rapport aux indications figurant sur le bon de commande.
            Toute réclamation formulée au-delà de ce délai sera rejetée.
          </p>
          <p>
            Dans le cas où l'emballage ne serait pas ouvert, le CLIENT dispose d'un délai
            de 48 heures à compter de la réception du produit pour formuler les réclamations
            visées au précédent alinéa, par email à l'adresse :
            {' '}<a href={`mailto:${COMPANY.email}`} className="text-[#E8945A] underline">{COMPANY.email}</a>.
            Toute réclamation non effectuée dans les règles définies ci-dessus et dans les
            délais impartis ne pourra être prise en compte.
          </p>
        </Section>

        <Section id="art-12" title="Article 12 – Utilisation – Entretien">
          <p>
            Le CLIENT s'engage à utiliser le matériel loué conformément à sa destination
            usuelle et à ne rien faire ou laisser faire qui puisse le détériorer. Pendant
            toute la durée de mise à disposition, le CLIENT est tenu de protéger le linge
            loué contre toute dégradation, vandalisme, surcharge, intempéries (pluie, vent,
            neige, gel…).
          </p>
          <p>
            Le CLIENT s'interdit formellement d'entretenir, par une autre entreprise et sous
            quelque prétexte que ce soit, du linge appartenant à {COMPANY.name}. Toute
            violation de cet engagement l'exposerait à payer immédiatement la valeur de
            l'intégralité des articles mis à sa disposition. Tout dommage occasionné au
            textile par abus d'usage, usage non approprié, négligence ou non-respect des
            recommandations engage le CLIENT à indemniser {COMPANY.name} selon l'évaluation
            retenue par le prestataire.
          </p>
        </Section>

        <Section id="art-13" title="Article 13 – Restitution – Pénalités – Tarif de remplacement">
          <p>
            En cas de difficultés ou de changement dans les conditions ou dans les délais de
            restitution du linge, le CLIENT s'engage à informer {COMPANY.name} par email à
            {' '}<a href={`mailto:${COMPANY.email}`} className="text-[#E8945A] underline">{COMPANY.email}</a>.
          </p>
          <p>
            En cas de retard de restitution, de non-restitution, de vol ou en cas de restitution
            d'un linge détérioré, il sera facturé au CLIENT le montant de remplacement par article
            selon le tableau ci-dessous :
          </p>

          <div className="overflow-x-auto rounded-xl border border-gray-200 my-6" data-testid="cgv-replacement-table">
            <table className="w-full text-sm">
              <thead className="bg-[#2D3E50] text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Article</th>
                  <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">Prix unitaire TTC</th>
                </tr>
              </thead>
              <tbody>
                {REPLACEMENT_TABLE.map((row, i) => (
                  <tr key={row.article} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}>
                    <td className="px-4 py-3 text-[#2D3E50]">{row.article}</td>
                    <td className="px-4 py-3 text-right font-medium text-[#E8945A]">{row.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p>
            Le CLIENT accepte d'ores et déjà que les sommes facturées en cas de retard, de
            non-restitution ou de détérioration soient prélevées sur la carte bancaire ayant
            servi à la transaction (voir empreinte de caution, Art. 16).
          </p>
          <p>
            En cas de restitution hors le lieu de prise de possession, il sera prélevé et
            facturé au CLIENT les frais de transport ou d'acheminement afférents, ce que ce
            dernier accepte d'ores et déjà.
          </p>
        </Section>

        <Section id="art-14" title="Article 14 – Sac à linge">
          <p>
            Le CLIENT est informé que {COMPANY.name} procède à la location de son linge dans
            des sacs à linge afin de préserver et d'identifier celui-ci. Le CLIENT doit
            restituer le sac, dans les conditions de délais visées à l'Article 13, en même
            temps que son linge, faute de quoi il sera redevable d'une indemnité correspondant
            à une fois et demie le coût d'achat dudit sac.
          </p>
        </Section>

        <Section id="art-15" title="Article 15 – Garantie">
          <p>
            {COMPANY.name} garantit le CLIENT contre tout défaut de conformité des services
            et tout vice caché provenant d'un défaut de conception ou de fourniture desdits
            services à l'exclusion de toute négligence ou faute du CLIENT. En tout état de
            cause, au cas où la responsabilité du prestataire serait retenue, la garantie
            serait limitée au montant HT payé par le CLIENT pour la fourniture des services.
          </p>
        </Section>

        <Section id="art-16" title="Article 16 – Caution & empreinte de carte bancaire">
          <div className="rounded-xl border-2 border-[#5493CC]/30 bg-[#F2F8FD] p-5">
            <p className="font-semibold text-[#2D3E50] mb-2">
              Empreinte de carte bancaire — Caution maximum 150 €
            </p>
            <p>
              Lors de la commande, l'empreinte de la carte bancaire du CLIENT est conservée
              à titre de <strong>caution</strong> pour couvrir les éventuels vols, pertes ou
              dégradations du <strong>linge de lit</strong> (draps, housses de couette, taies)
              et des <strong>serviettes</strong> loués.
            </p>
            <p className="mt-3">
              Cette caution s'élève à un maximum de <strong>150 € (cent cinquante euros)</strong>
              {' '}par commande.
            </p>
            <p className="mt-3">
              En cas de vol, perte ou détérioration constatés à la restitution, {COMPANY.name}
              {' '}est <strong>expressément autorisée</strong> par le CLIENT à prélever sur cette
              empreinte le montant correspondant aux articles non restitués ou détériorés, dans
              la limite de 150 €, conformément au tarif de remplacement figurant à l'Article 13.
            </p>
            <p className="mt-3">
              Si le préjudice excède 150 €, le solde restera dû par le CLIENT et sera réclamé
              par tous moyens. En l'absence de litige à la restitution, l'empreinte est
              automatiquement libérée et aucun montant n'est prélevé.
            </p>
          </div>
        </Section>

        <Section id="art-17" title="Article 17 – Transfert des risques – Réserve de propriété">
          <p>
            Le transfert des risques de perte et de détérioration s'effectue à la prise de
            possession du linge par le CLIENT. Les produits voyagent aux risques et périls
            de {COMPANY.name} jusqu'à la livraison. Le CLIENT est seul responsable de toute
            détérioration, perte, vol ou destruction quelle qu'en soit la cause à compter de
            la livraison. {COMPANY.name} conserve l'entière propriété des biens loués et
            pourra revendiquer entre quelques mains que ce soit lesdits biens, ou leur
            contre-valeur.
          </p>
        </Section>

        <Section id="art-18" title="Article 18 – Force majeure">
          <p>
            Toutes circonstances indépendantes de la volonté des parties, empêchant l'exécution
            dans des conditions normales de leurs obligations, sont considérées comme des
            causes d'exonération des obligations des parties et entraînent leur suspension.
            La partie qui invoque les circonstances visées ci-dessus doit avertir immédiatement
            l'autre partie de leur survenance, ainsi que de leur disparition.
          </p>
        </Section>

        <Section id="art-19" title="Article 19 – Propriété intellectuelle">
          <p>
            Le contenu du site internet (documents techniques, dessins, photographies, textes,
            logos, etc.) reste la propriété de {COMPANY.name}, seul titulaire des droits de
            propriété intellectuelle sur ce contenu. Les acheteurs s'engagent à ne faire aucun
            usage de ce contenu ; toute reproduction totale ou partielle de ce contenu est
            strictement interdite et est susceptible de constituer un délit de contrefaçon.
          </p>
        </Section>

        <Section id="art-20" title="Article 20 – Informatique et Libertés">
          <p>
            Les données nominatives fournies par le CLIENT sont nécessaires au traitement de
            sa commande et à l'établissement des factures. Elles peuvent être communiquées
            aux partenaires de {COMPANY.name} chargés de l'exécution, du traitement, de la
            gestion et du paiement des commandes. Conformément au Règlement Général sur la
            Protection des Données (RGPD) et à la loi Informatique et Libertés, le CLIENT
            dispose d'un droit d'accès permanent, de modification, de rectification et
            d'opposition concernant les informations le concernant, exerçable par email à
            {' '}<a href={`mailto:${COMPANY.email}`} className="text-[#E8945A] underline">{COMPANY.email}</a>.
          </p>
        </Section>

        <Section id="art-21" title="Article 21 – Non-validation partielle – Non-renonciation">
          <p>
            Si une ou plusieurs stipulations des présentes conditions générales sont tenues
            pour non valides ou déclarées telles en application d'une loi, d'un règlement ou
            à la suite d'une décision définitive d'une juridiction compétente, les autres
            stipulations garderont toute leur force et leur portée.
          </p>
        </Section>

        <Section id="art-22" title="Article 22 – Renonciation au droit de rétractation – Aucun remboursement">
          <div className="rounded-xl border-2 border-[#E8945A]/40 bg-[#FFFBF5] p-5">
            <p className="font-semibold text-[#2D3E50] mb-2 uppercase">
              Toute commande validée et payée est ferme et définitive — aucun remboursement.
            </p>
            <p>
              Conformément à l'article <strong>L.221-28 1° du Code de la consommation</strong>,
              le droit de rétractation ne peut être exercé pour les contrats de fourniture
              de services pleinement exécutés avant la fin du délai de rétractation et dont
              l'exécution a commencé après accord préalable exprès du consommateur et
              renoncement exprès à son droit de rétractation.
            </p>
            <p className="mt-3">
              <strong>
                En validant sa commande sur le site de {COMPANY.name}, le CLIENT demande
                expressément l'exécution immédiate du service de location et de livraison
                de linge, et renonce expressément à son droit de rétractation.
              </strong>
            </p>
            <p className="mt-3">
              En conséquence, <strong>aucune commande validée et payée ne pourra faire
              l'objet d'un remboursement</strong>, qu'il soit total ou partiel, à l'initiative
              du CLIENT, y compris en cas d'annulation du séjour, de modification de date,
              de non-utilisation du linge ou de restitution anticipée.
            </p>
            <p className="mt-3 text-sm text-[#64748B]">
              Seuls les cas d'impossibilité avérée d'exécution de la prestation imputable
              à {COMPANY.name} pourront, à l'appréciation exclusive du prestataire, donner
              lieu à un avoir ou à un remboursement.
            </p>
          </div>
        </Section>

        <Section id="art-23" title="Article 23 – Loi applicable – Attribution de juridiction">
          <p>
            Les présentes conditions générales sont soumises à l'application du droit français,
            à l'exclusion des dispositions de la convention de Vienne. Il en est ainsi pour
            les règles de fond comme pour les règles de forme. En cas de litige ou de
            réclamation, le CLIENT s'adressera en priorité à {COMPANY.name} pour obtenir une
            solution amiable. Si elles n'y parviennent pas, les parties soumettront le litige
            aux Tribunaux du ressort du siège social du prestataire.
          </p>
        </Section>

        {/* Footer CTA */}
        <div className="mt-16 pt-10 border-t border-gray-100 text-center">
          <p className="text-[#64748B] mb-6">
            Une question sur nos conditions de location ?
          </p>
          <Button
            asChild
            className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg h-12 px-8"
            data-testid="cgv-contact-btn"
          >
            <a href={`mailto:${COMPANY.email}`}>Nous contacter par email</a>
          </Button>
          <p className="text-xs text-[#64748B] mt-4">
            {COMPANY.email} — {COMPANY.hours}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CGVPage;
